import { getApp, getApps, initializeApp } from "firebase/app";
import {
  deleteToken as deleteMessagingToken,
  getMessaging,
  getToken as getMessagingToken,
  isSupported,
  onMessage as onForegroundMessage,
} from "firebase/messaging";

const INITIAL_NOTIFICATION_PARAM = "__pushData";
const SERVICE_WORKER_URL = "/firebase-messaging-sw.js";
const FIREBASE_CONFIG = {
  apiKey: process.env.FIREBASE_API_KEY || "",
  authDomain: process.env.FIREBASE_AUTH_DOMAIN || "",
  projectId: process.env.FIREBASE_PROJECT_ID || "",
  storageBucket: process.env.FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: process.env.FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.FIREBASE_APP_ID || "",
};
const VAPID_KEY = process.env.FIREBASE_VAPID_KEY || "";

let cachedInitialNotification;
let hasCheckedInitialNotification = false;
let messagingPromise = null;
let serviceWorkerPromise = null;
let browserSupportPromise = null;
let hasWarnedMissingConfig = false;

const NOOP = () => {};
const AUTH_STATUS = {
  NOT_DETERMINED: -1,
  DENIED: 0,
  AUTHORIZED: 1,
  PROVISIONAL: 2,
};

const hasWindow = () => typeof window !== "undefined";
const hasNavigator = () => typeof navigator !== "undefined";

const hasFirebaseConfig = () =>
  Boolean(
    FIREBASE_CONFIG.apiKey &&
      FIREBASE_CONFIG.projectId &&
      FIREBASE_CONFIG.messagingSenderId &&
      FIREBASE_CONFIG.appId
  );

const logMissingConfig = () => {
  if (hasWarnedMissingConfig || hasFirebaseConfig()) {
    return;
  }

  hasWarnedMissingConfig = true;
  console.warn(
    "Web notifications are not configured. Add FIREBASE_* values and FIREBASE_VAPID_KEY to the web env file."
  );
};

const normalizeNotificationPayload = (payload) => {
  if (!payload) {
    return null;
  }

  const data =
    payload.data && typeof payload.data === "object"
      ? { ...payload.data }
      : {};

  const notification =
    payload.notification && typeof payload.notification === "object"
      ? {
          title: payload.notification.title || data.title || "",
          body: payload.notification.body || data.body || "",
          image: payload.notification.image || data.image || undefined,
        }
      : data.title || data.body
        ? {
            title: data.title || "",
            body: data.body || "",
            image: data.image || undefined,
          }
        : undefined;

  return {
    data,
    notification,
  };
};

const readInitialNotificationFromUrl = () => {
  if (!hasWindow()) {
    return null;
  }

  const url = new URL(window.location.href);
  const encodedPayload = url.searchParams.get(INITIAL_NOTIFICATION_PARAM);
  if (!encodedPayload) {
    return null;
  }

  url.searchParams.delete(INITIAL_NOTIFICATION_PARAM);
  window.history.replaceState({}, document.title, url.toString());

  try {
    const parsedPayload = JSON.parse(decodeURIComponent(encodedPayload));
    return normalizeNotificationPayload(parsedPayload);
  } catch (error) {
    console.warn("Failed to parse initial web notification payload:", error);
    return null;
  }
};

const resolveInitialNotification = () => {
  if (!hasCheckedInitialNotification) {
    cachedInitialNotification = readInitialNotificationFromUrl();
    hasCheckedInitialNotification = true;
  }

  return cachedInitialNotification;
};

const ensureBrowserSupport = async () => {
  if (!hasWindow() || !hasNavigator() || !hasFirebaseConfig()) {
    logMissingConfig();
    return false;
  }

  if (!browserSupportPromise) {
    browserSupportPromise = isSupported().catch((error) => {
      console.warn("Web messaging support check failed:", error);
      return false;
    });
  }

  return browserSupportPromise;
};

const ensureFirebaseApp = () => {
  if (!hasFirebaseConfig()) {
    logMissingConfig();
    return null;
  }

  if (getApps().length > 0) {
    return getApp();
  }

  return initializeApp(FIREBASE_CONFIG);
};

const ensureServiceWorkerRegistration = async () => {
  if (!hasNavigator() || !("serviceWorker" in navigator)) {
    return null;
  }

  if (!serviceWorkerPromise) {
    serviceWorkerPromise = navigator.serviceWorker
      .register(SERVICE_WORKER_URL)
      .catch((error) => {
        console.warn("Web notification service worker registration failed:", error);
        return null;
      });
  }

  return serviceWorkerPromise;
};

const ensureMessagingInstance = async () => {
  const supported = await ensureBrowserSupport();
  if (!supported) {
    return null;
  }

  if (!messagingPromise) {
    messagingPromise = Promise.resolve().then(() => {
      const app = ensureFirebaseApp();
      return app ? getMessaging(app) : null;
    });
  }

  return messagingPromise;
};

const mapPermissionToStatus = (permission) => {
  if (permission === "granted") {
    return AUTH_STATUS.AUTHORIZED;
  }

  if (permission === "denied") {
    return AUTH_STATUS.DENIED;
  }

  return AUTH_STATUS.NOT_DETERMINED;
};

const requestBrowserPermission = async () => {
  if (!hasWindow() || typeof Notification === "undefined") {
    return AUTH_STATUS.DENIED;
  }

  if (Notification.permission === "granted") {
    return AUTH_STATUS.AUTHORIZED;
  }

  const permission = await Notification.requestPermission();
  return mapPermissionToStatus(permission);
};

const buildServiceWorkerMessage = (rawPayload) => {
  if (!rawPayload || typeof rawPayload !== "object") {
    return null;
  }

  const payload =
    rawPayload.type === CLICK_EVENT && rawPayload.payload
      ? rawPayload.payload
      : rawPayload;

  return normalizeNotificationPayload(payload);
};

const registerNotificationClickListener = (callback) => {
  const handleServiceWorkerMessage = (event) => {
    const normalizedPayload = buildServiceWorkerMessage(event?.data);
    if (!normalizedPayload) {
      return;
    }

    callback(normalizedPayload);
  };

  navigator.serviceWorker?.addEventListener("message", handleServiceWorkerMessage);

  return () => {
    navigator.serviceWorker?.removeEventListener("message", handleServiceWorkerMessage);
  };
};

const messaging = () => ({
  async getToken() {
    const permissionStatus = await requestBrowserPermission();
    if (
      permissionStatus !== AUTH_STATUS.AUTHORIZED &&
      permissionStatus !== AUTH_STATUS.PROVISIONAL
    ) {
      return null;
    }

    const webMessaging = await ensureMessagingInstance();
    const serviceWorkerRegistration = await ensureServiceWorkerRegistration();

    if (!webMessaging || !serviceWorkerRegistration || !VAPID_KEY) {
      if (!VAPID_KEY) {
        console.warn("FIREBASE_VAPID_KEY is missing. Web push token cannot be created.");
      }
      return null;
    }

    return getMessagingToken(webMessaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration,
    });
  },

  async deleteToken() {
    const webMessaging = await ensureMessagingInstance();
    if (!webMessaging) {
      return;
    }

    await deleteMessagingToken(webMessaging);
  },

  requestPermission: requestBrowserPermission,

  onMessage(callback) {
    let unsubscribe = NOOP;
    let isMounted = true;

    ensureMessagingInstance()
      .then((webMessaging) => {
        if (!isMounted || !webMessaging) {
          return;
        }

        unsubscribe = onForegroundMessage(webMessaging, (payload) => {
          const normalizedPayload = normalizeNotificationPayload(payload);
          if (normalizedPayload) {
            callback(normalizedPayload);
          }
        });
      })
      .catch((error) => {
        console.warn("Web foreground notification subscription failed:", error);
      });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  },

  onNotificationOpenedApp(callback) {
    return registerNotificationClickListener(callback);
  },

  onTokenRefresh() {
    return NOOP;
  },

  async getInitialNotification() {
    return resolveInitialNotification();
  },

  setBackgroundMessageHandler: NOOP,

  async registerDeviceForRemoteMessages() {
    await ensureServiceWorkerRegistration();
  },
});

messaging.AuthorizationStatus = AUTH_STATUS;

export default messaging;
